import { useState, useEffect, useRef } from 'react';

// Generic searchable replacement for a plain <select> — filters options as
// you type, click (or Enter) to pick. `options` items need at minimum an
// `id` and a `label`; pass `searchKeys` to also match on other fields
// (e.g. a code, unit, or site name) beyond the label itself.
export default function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Search…',
  searchKeys = [],
  renderOption,   // optional (option) => ReactNode for the option row's main content
  renderExtra,    // optional (option) => ReactNode, shown right-aligned in the option row
  disabled = false,
  required = false,
  className = 'input',
}) {
  const [search, setSearch] = useState('');
  const [open,   setOpen]   = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const selected = options.find(o => String(o.id) === String(value));

  const filtered = options.filter(o => {
    if (!search) return true;
    const q = search.toLowerCase();
    if (o.label?.toLowerCase().includes(q)) return true;
    return searchKeys.some(key => String(o[key] ?? '').toLowerCase().includes(q));
  });

  return (
    <div className="relative" ref={ref}>
      <input
        type="text"
        className={className}
        disabled={disabled}
        required={required && !value}
        placeholder={selected ? selected.label : placeholder}
        value={open ? search : (selected ? selected.label : '')}
        onChange={e => { setSearch(e.target.value); setOpen(true); }}
        onFocus={() => { setSearch(''); setOpen(true); }}
        autoComplete="off"
      />
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-400">No matches</div>
          ) : (
            filtered.map(o => (
              <button
                key={o.id}
                type="button"
                className={`w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 flex items-center justify-between gap-2 ${String(value) === String(o.id) ? 'bg-indigo-50 font-medium' : ''}`}
                onMouseDown={e => e.preventDefault()}
                onClick={() => { onChange(o.id); setSearch(''); setOpen(false); }}>
                <span className="truncate">{renderOption ? renderOption(o) : o.label}</span>
                {renderExtra && <span className="flex-shrink-0">{renderExtra(o)}</span>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
